import styled from "@emotion/styled";
import PropertyPanel from "./components/property-panel/PropertyPanel";
import { Box, Grid, IconButton } from "@mui/material";
import { useGetDetailPropertyQuery } from "@/src-refactor/modules/store/qrtk/services/realestate/realestate.qrtk";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useRef } from "react";
import RealEstateDetailsContext, {
  useRealEstateDetailContext
} from "./context/realestate-details.context";
import MainDetailProperty from "./components/main-detail-property/MainDetailProperty";
import AgentsCard from "./components/agents-card/AgentsCard";
import DocumentsCard from "./components/documents-card/DocumentsCard";
import OwnersCard from "./components/owners-card/OwnersCard";
import ProfessionalFeesCard from "./components/professional-fees-card/ProfessionalFeesCard";
import AlterestateCard from "./components/alterestate-card/AlterestateCard";
import DataLog from "./components/data-log/DataLog";
import BackIcon from "@icons-rf/property-panel/arrow-back.svg?react";
import { detailsPropertiesAdapter } from "@/src-refactor/modules/private/realestate/adapters/properties/details-properties.adapter";
import AssessmentModal from "../../components/modals/assessment-modal/AssessmentModal";
import StatisticsCard from "./components/statistics-card/StatisticsCard";
import MxEmptyCard from "@rf-l1/empty-sections/MxEmptyCard";
import MxEmptyBody from "@rf-l1/empty-sections/MxEmptyBody";
import MxLoading from "@rf-l1/loading/MxLoading";
import MxEmptyIcon from "@rf-l1/empty-sections/MxEmptyIcon";
import WarningCard from "./components/warnings/WarningsCard";
import ValidationDocuments from "./components/validation-documents-card/ValidationDocuments";
import { useWidth } from "@/shared/hooks/use-width.hook";
import MxDetailLayoutMain from "@rf-l1/page/details/MxDetailLayoutMain";
import { createPortal } from "react-dom";
import { QueryStatus } from "@reduxjs/toolkit/query";
import PropertyDetailChangeLog from "@rf/property/module/pages/detail/components/change-log/PropertyDetailChangeLog";
import PropertyDetailRemaxGlobalCard from "@rf/property/module/pages/detail/components/remax-global/PropertyDetailRemaxGlobalCard";
import UseQuery from "@/shared/hooks/use-query.hook";
import { debounce } from "lodash";
import LoadReview from "./LoadReview";

function RealestateDetailPage() {
  const { id } = useParams<{ id: string }>();

  const {
    data: GET_DETAIL_PROPERTY_DATA,
    status: GET_DETAIL_PROPERTY_STATUS,
    refetch: REFECTH_DETAIL_PROPERTY,
    isFetching,
    isLoading,
    isError,
    error
  } = useGetDetailPropertyQuery(
    {
      params: {
        id: id ?? ""
      }
    },
    {
      skip: !Boolean(id)
    }
  );

  if (!Boolean(id) || !id)
    return (
      <MxEmptyCard size="page">
        <MxEmptyIcon variant="404" />
        <MxEmptyBody>Destino incierto</MxEmptyBody>
      </MxEmptyCard>
    );

  if (isLoading && !Boolean(GET_DETAIL_PROPERTY_DATA))
    return (
      <ContentLoading>
        <MxLoading variant="gradient" />
      </ContentLoading>
    );

  if (isError) {
    console.log(error);
    if (error["status"] == 404)
      return (
        <MxEmptyCard size="page">
          <MxEmptyIcon variant="404" />
          <MxEmptyBody>No se encontro esta propiedad</MxEmptyBody>
        </MxEmptyCard>
      );
    else
      return (
        <MxEmptyCard size="page">
          <MxEmptyIcon variant="error" />
          <MxEmptyBody>A ocurrido un problema</MxEmptyBody>
        </MxEmptyCard>
      );
  }

  return (
    <MxDetailLayoutMain>
      <RealEstateDetailsContext propertyData={GET_DETAIL_PROPERTY_DATA!}>
        <Content />
      </RealEstateDetailsContext>
    </MxDetailLayoutMain>
  );
}

export default RealestateDetailPage;

const Content = () => {
  const width = useWidth();
  const isMobile = ["xs", "sm"].includes(width);
  const {
    PROPERTY_DETAIL: { principalData }
  } = useRealEstateDetailContext();
  return (
    <>
      <LoadReview />
      <Grid container spacing={2} paddingBottom={10}>
        {!isMobile &&
          (() => {
            const toolBar =
              typeof document !== "undefined"
                ? document.getElementById("tool-bar")
                : null;
            return toolBar
              ? createPortal(
                  <NavBarButton>
                    <BackButton
                      disableRipple
                      onClick={() => window.close()}
                      size="small"
                    >
                      <BackIcon />
                    </BackButton>
                  </NavBarButton>,
                  toolBar
                )
              : null;
          })()}

        <Grid item xs={12} lg={8.5} md={12} sm={8}>
          <MainDetailProperty />

          {isMobile && (
            <Grid marginTop={2} marginBottom={-2}>
              <PropertyPanel />
            </Grid>
          )}

          {!isMobile && <DataLog />}
          <Box my={2}>
            <PropertyDetailChangeLog />
          </Box>
          {/* <ChangesLog /> */}
          {/* <PDFTrial /> */}
        </Grid>
        <Grid item xs={12} lg={3.5} md={12} sm={4}>
          {!isMobile && <PropertyPanel />}
          <WarningCard />
          <AgentsCard />
          <OwnersCard />
          <StatisticsCard />
          <PropertyDetailRemaxGlobalCard status={principalData?.remaxGlobal} />
          {/* <CollectionCard /> */}
          <ProfessionalFeesCard />
          <DocumentsCard />
          <ValidationDocuments />
          <AlterestateCard />
          {isMobile && <DataLog />}
        </Grid>
      </Grid>
      <AssessmentModal useDetailContext />
    </>
  );
};

const BackButton = styled(IconButton)`
  top: 10px;
  left: 10px;
`;

const NavBarButton = styled(Box)`
  position: relative;
  width: 25px;
  height: 25px;
  top: -15px;
  z-index: 10001;
  & .MuiButtonBase-root {
    background-color: var(--m-color-blue-03);
    border-radius: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;

    &:hover {
      background-color: var(--m-color-blue-03);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }

    svg {
      color: white;
      width: 20px;
      height: 20px;
    }
  }
`;

const ContentLoading = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  height: 70vh;
`;
